import { useDragLayer, useDrop } from "react-dnd"
import classNames from "classnames"
import { ID } from "@nova/constants"
import { WorkspaceEntityType } from "../../types"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { selectFavoritesData } from "../../context/selectors"
import { overwriteFavorites } from "../../context/thunks"
import { useRef } from "react"

type DroppableSeparatorProps = {
    acceptedType: WorkspaceEntityType | WorkspaceEntityType[]
    idAbove?: ID
    idBelow?: ID
}

export const DroppableSeparator = ({ acceptedType, idAbove, idBelow }: DroppableSeparatorProps) => {
    const dispatch = useAppDispatch()
    const { favoriteIds } = useAppSelector(selectFavoritesData)

    const [{ isOver, canDrop }, dropRef] = useDrop(
        () => ({
            accept: acceptedType,
            drop: async (item: { id: string; type: WorkspaceEntityType }) => {
                const mutableFavorites = Array.from(favoriteIds)

                if (mutableFavorites.includes(item.id)) {
                    const indexOfItemToDelete = mutableFavorites.indexOf(item.id)
                    mutableFavorites.splice(indexOfItemToDelete, 1)
                }

                if (idAbove !== undefined) {
                    const indexToInsertAfter = mutableFavorites.indexOf(idAbove)
                    mutableFavorites.splice(indexToInsertAfter + 1, 0, item.id)
                } else if (idBelow !== undefined) {
                    const indexToInsertBefore = mutableFavorites.indexOf(idBelow)
                    mutableFavorites.splice(indexToInsertBefore, 0, item.id)
                } else {
                    mutableFavorites.push(item.id)
                }

                dispatch(overwriteFavorites(mutableFavorites))
            },
            collect: (monitor) => ({
                isOver: !!monitor.isOver(),
                canDrop: !!monitor.canDrop(),
            }),
            canDrop: (item) => item.id !== idAbove && item.id !== idBelow,
        }),
        [favoriteIds, dispatch, acceptedType, idAbove, idBelow]
    )

    const anythingIsDragging = useDragLayer((monitor) => {
        const type = monitor.getItemType()
        const isAcceptableType =
            type === WorkspaceEntityType.Folder || type === WorkspaceEntityType.Project
        return monitor.isDragging() && isAcceptableType
    })

    const divRef = useRef<HTMLDivElement>(null)
    dropRef(divRef)

    return anythingIsDragging ? (
        <div
            ref={divRef}
            className={classNames("absolute top-[1.875rem] p-[0.3125rem] h-3 w-full z-50")}
        >
            <div
                className={classNames({
                    "bg-blue-500 w-full h-full": isOver ? canDrop : undefined,
                })}
            />
        </div>
    ) : null
}
